class WishlistButton extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute('handle');
    this.button = null;
  }

  static get wishlistKey() {
    return 'wishlist_products';
  }

  get wishlist() {
    return JSON.parse(localStorage.getItem(WishlistButton.wishlistKey)) || [];
  }

  set wishlist(items) {
    localStorage.setItem(WishlistButton.wishlistKey, JSON.stringify(items));
  }

  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.updateState();

    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle();
    });
  }

  isWishlisted() {
    return this.wishlist.includes(this.handle);
  }

  toggle() {
    let items = this.wishlist;
    const index = items.indexOf(this.handle);

    if (index > -1) {
      items.splice(index, 1);
    } else {
      items.push(this.handle);
    }

    this.wishlist = items;
    this.updateState();

    document.dispatchEvent(
      new CustomEvent('wishlist-updated', {detail: items}),
    );
  }

  updateState() {
    const isActive = this.isWishlisted();

    this.classList.toggle('is-wishlisted', isActive);

    if (this.button) {
      this.button.setAttribute('aria-pressed', isActive);
    }

    const icon = this.button.querySelector('svg');
    if (icon) {
      icon.classList.toggle('active', isActive);
    }
  }
}

customElements.define('wishlist-button', WishlistButton);

class WishlistProducts extends HTMLElement {
  constructor() {
    super();
    this.cart =
      document.querySelector('cart-notification') ||
      document.querySelector('cart-drawer');
    this.productContainer = this.querySelector('#wishlist-products-container');

    const customerWishlistData = this.dataset.customerWishlist;
    this.customerWishlist = customerWishlistData
      ? JSON.parse(customerWishlistData)
      : null;
  }

  connectedCallback() {
    this.render();
    this.loadWishlist();
  }

  render() {
    this.productContainer.innerHTML = `<p>Loading your wishlist...</p>`;
  }

  loadWishlist() {
    const container = this.productContainer;

    // Read from metafield
    let metafieldHandles = Array.isArray(this.customerWishlist)
      ? this.customerWishlist
      : [];

    // Read from localStorage
    let localHandles = JSON.parse(
      localStorage.getItem('wishlist_products') || '[]',
    );

    // Merge them, removing duplicates
    const combinedHandles = Array.from(
      new Set([...metafieldHandles, ...localHandles]),
    );

    if (combinedHandles.length === 0) {
      container.innerHTML =
        '<p class="empty-wishlist">Your wishlist is empty</p>';
      return;
    }

    Promise.all(
      combinedHandles.map((handle) =>
        fetch(`/?sections=wishlist-card-product&product_handle=${handle}`)
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data && data['wishlist-card-product']) {
              return {html: data['wishlist-card-product'], handle};
            }
            return null;
          })
          .catch(() => null),
      ),
    ).then((results) => {
      const validItems = results.filter((item) => item !== null);

      if (validItems.length === 0) {
        container.innerHTML =
          '<p class="empty-wishlist">Could not load wishlist</p>';
        return;
      }

      container.innerHTML = '';
      validItems.forEach((item) => {
        const wrapper = document.createElement('div');
        wrapper.classList.add(
          'wishlisit-item',
          'leo-mb-8',
          'last:leo-mb-0',
          'leo-border-b',
          'leo-border-brand-grey-300',
          'last:leo-border-0',
        );
        wrapper.innerHTML = item.html;

        const btn = wrapper.querySelector('.remove-btn');
        btn.addEventListener('click', () => {
          this.removeFromWishlist(item.handle);
          wrapper.remove();

          if (!container.querySelector('.wishlisit-item')) {
            container.innerHTML =
              '<p class="empty-wishlist">Your wishlist is empty</p>';
          }
        });
        container.appendChild(wrapper);
      });
      this.addToCartEvents();
    });
  }

  addToCartEvents() {
    const singleAddToCartBtns = this.productContainer.querySelectorAll(
      'button[data-variant-id]',
    );
    const allAddToCartBtns = this.querySelector('#add-aw-products');
    const selectedAddToCartBtns = this.querySelector('#add-asw-products');

    singleAddToCartBtns.forEach((button) => {
      button.addEventListener('click', (e) => {
        const variantId = button.getAttribute('data-variant-id');

        const products = {
          items: [
            {
              id: parseInt(variantId),
              quantity: 1,
            },
          ],
        };

        this.addToCart(button, products);
      });
    });

    allAddToCartBtns.addEventListener('click', () => {
      const products = {
        items: [],
      };

      singleAddToCartBtns.forEach((button) => {
        const variantId = button.getAttribute('data-variant-id');
        if (variantId) {
          products.items.push({
            id: parseInt(variantId),
            quantity: 1,
          });
        }
      });

      this.addToCart(allAddToCartBtns, products);
    });

    selectedAddToCartBtns.addEventListener('click', () => {
      const checkedProducts = this.querySelectorAll(
        'input[type="checkbox"]:checked',
      );

      if (checkedProducts.length > 0) {
        const products = {
          items: [],
        };

        checkedProducts.forEach((input) => {
          const variantId = input.value;
          if (variantId) {
            products.items.push({
              id: parseInt(variantId),
              quantity: 1,
            });
          }
        });

        this.addToCart(selectedAddToCartBtns, products);
      } else {
        new ShowToast('error', 'Please select a product');
      }
    });
  }

  addToCart(button, products) {
    button.classList.add('adding');
    button.disabled = true;

    const payload = {
      ...products,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((err) => {
            throw new Error(err.description);
          });
        }
        return res.json();
      })
      .then((response) => {
        if (response.sections && response.items && response.items.length > 0) {
          response.items[0].sections = response.sections;
        }
        if (this.cart.tagName.toLowerCase() === 'cart-notification') {
          new ShowToast('success', 'Successfully added to cart');
        } else if (this.cart.tagName.toLowerCase() === 'cart-drawer') {
          this.cart.classList.remove('is-empty');
          this.cart.renderContents(response);
        }
      })
      .catch(() => {
        button.textContent = 'Error';
      })
      .finally((response) => {
        button.classList.remove('adding');
        button.disabled = false;
      });
  }
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
  removeFromWishlist(handle) {
    const list = JSON.parse(localStorage.getItem('wishlist_products') || '[]');
    const updated = list.filter((h) => h !== handle);
    localStorage.setItem('wishlist_products', JSON.stringify(updated));
    document.dispatchEvent(new CustomEvent('wishlist-updated', {detail: list}));
  }
}

customElements.define('wishlist-products', WishlistProducts);

class WishlistCartBubble extends HTMLElement {
  constructor() {
    super();
    this.bubble = this.querySelector('.wishlist-count-bubble');
    this.isPrependZeroEnabled = this.bubble?.dataset.prependZero === 'true';
  }

  connectedCallback() {
    this.render();
    document.addEventListener('wishlist-updated', this.handleWishlistUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist-updated', this.handleWishlistUpdate);
  }

  handleWishlistUpdate = (e) => {
    this.render();
  };

  render() {
    const wishlistCount = JSON.parse(
      localStorage.getItem('wishlist_products') || '[]',
    ).length;

    let displayCount = wishlistCount;
    if (this.isPrependZeroEnabled) {
      displayCount =
        wishlistCount < 10 ? `0${wishlistCount}` : `${wishlistCount}`;
    }

    if (this.bubble) {
      this.bubble.textContent = displayCount;
      this.bubble.style.display = wishlistCount > 0 ? 'inline-block' : 'none';
    }

    if (wishlistCount > 0) {
      this.classList.remove('hidden');
      this.classList.add('lg:leo-block');
    } else if (wishlistCount === 0 && !this.classList.contains('hidden')) {
      this.classList.add('hidden');
      this.classList.remove('lg:leo-block');
    }
  }
}

customElements.define('wishlist-count-bubble', WishlistCartBubble);
